// @flow
import React, { Component } from 'react';
import i18n from 'app/common/lib/i18n';
import logStats from 'app/common/lib/logStats';
import type { GoogleApiResponse } from 'app/packages/pinterest-authentication/google';
import { Box, IconButton } from 'gestalt';

// https://docs.microsoft.com/en-us/microsoft-edge/hosting/webview/scriptnotifyevent
export type ScriptNotifyEvent = {
  callingUri: string,
  value: string,
};

export type ScriptNotifyEventType = 'MSWebViewScriptNotify';
export type ScriptNotifyEventHandler = (event: ScriptNotifyEvent) => mixed;
export type ScriptNotifyEventListener =
  | { handleEvent: ScriptNotifyEventHandler }
  | ScriptNotifyEventHandler;

export type MSHTMLWebViewElement = {
  addEventListener(type: ScriptNotifyEventType, listener: ScriptNotifyEventListener): void,
};

const GOOGLE_AUTH_ID_TOKEN = 'google_auth_id_token';
const GOOGLE_AUTH_FIRST_NAME = 'google_auth_first_name';

const styles = {
  container: {
    __style: {
      backgroundColor: 'white',
      borderRadius: '8px',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
    },
  },
  msWebview: {
    height: '100%',
    width: '100%',
  },
};

const initialWebviewSource = () =>
  typeof window !== 'undefined' ? `${window.location.origin}/connect/gplus/` : '';

type Props = {
  onDismiss: () => void,
  onError: () => void,
  onSignInCallback: (
    authResult: GoogleApiResponse,
    isAutologin: boolean,
    gapi: Object,
    connectType: string,
  ) => void,
};

type State = {
  googleSignWebviewSource: string,
};

class WindowsPWAWebView extends Component<Props, State> {
  state: State = {
    googleSignWebviewSource: initialWebviewSource(),
  };

  handleMsWebViewScriptNotify = (e: ScriptNotifyEvent) => {
    if (e.value === 'success') {
      const { onDismiss, onSignInCallback } = this.props;

      const idToken: string = window.localStorage.getItem(GOOGLE_AUTH_ID_TOKEN);
      const firstName: string = window.localStorage.getItem(GOOGLE_AUTH_FIRST_NAME);

      window.localStorage.removeItem(GOOGLE_AUTH_ID_TOKEN);
      window.localStorage.removeItem(GOOGLE_AUTH_FIRST_NAME);

      logStats.increment('windows.google.connect.handleMsWebViewScriptNotify.success');

      onSignInCallback(
        {
          access_token: '',
          code: '',
          expires_at: '',
          id_token: idToken,
          status: {
            signed_in: true,
          },
          first_name: firstName,
        },
        false,
        window.gapi,
        'windows',
      );

      onDismiss();
    } else if (e.value === 'failure') {
      logStats.increment('windows.google.connect.handleMsWebViewScriptNotify.failure');
      this.props.onError();
    }
  };

  msWebViewRef = (webview: ?HTMLElement) => {
    if (!webview) {
      return;
    }

    ((webview: any): MSHTMLWebViewElement).addEventListener(
      'MSWebViewScriptNotify',
      this.handleMsWebViewScriptNotify,
    );
  };

  render() {
    const { onDismiss } = this.props;
    const { googleSignWebviewSource } = this.state;
    return (
      <Box
        display="flex"
        position="fixed"
        dangerouslySetInlineStyle={styles.container}
        direction="column"
        height="800px"
        padding={1}
        width="500px"
      >
        <Box alignItems="end" display="flex" height="35px" justifyContent="end">
          <IconButton
            accessibilityLabel={i18n._(
              'Cancel Google Sign-In',
              'Accessibility label for Google Sign-In Dialog bog',
            )}
            onClick={onDismiss}
            icon="cancel"
          />
        </Box>
        <Box flex="grow">
          <x-ms-webview
            style={styles.msWebview}
            ref={this.msWebViewRef}
            src={googleSignWebviewSource}
          />
        </Box>
      </Box>
    );
  }
}

export default WindowsPWAWebView;
